"use client";

import {toolRequestBody} from "./toolRequestBody";

import clsx from "clsx";
import {ApprovalStatusBanner, approvalVisualState, approvalBannerLabel} from "@repo/ui/gailgpt/approval-status-banner";
import type {TFunction} from "i18next";
import ApprovalReviewScrollArea from "./ApprovalReviewScrollArea";
import {useId, useLayoutEffect, useRef, useState} from "react";
import ApprovalDecisionStatus, {selectionClass, selectionLabelKey} from "./ApprovalDecisionStatus";
import {proposalTitle} from "./proposalIdentity";
import type {ApprovalDecisionOutcome, ApprovalPosition} from "@/types/chatMessages";
import type {MutationProposal} from "@/services/wingman/mutation-proposal";
import UseCaseSnapshotDiff from "./UseCaseSnapshotDiff";
import ScriptSnapshotDiff, {changedFields} from "./ScriptSnapshotDiff";
import ScriptApprovalHeader from "./ScriptApprovalHeader";
import ScriptApprovalReceipt, {OpenScriptButton} from "./ScriptApprovalReceipt";
import Button from "@/components/library/Button";
import Icon from "@/components/library/Icon";
import {useTranslation} from "react-i18next";
import type {MessageContentFunctionCall, ToolApprovalResponse} from "@/services/wingman";
import ToolApprovalNotices from "./ToolApprovalNotices";
import {useToolApprovalDecision} from "./useToolApprovalDecision";
import {ToolDetailsContent} from "./ToolDetails";
import {approvalPresentation, approvalStatusKey} from "./approvalPresentation";
import {displayToolPresentation} from "@repo/ui/gailgpt/tool-display";
import {presentationText} from "@repo/ui/gailgpt/tool-presentation";
import WorkflowPublishApproval from "./WorkflowPublishApproval";
import {readPublicationWorkflow} from "./workflowPublicationSummary";
import {ApprovalReferenceProvider} from "./ApprovalReferenceValue";
import {ApprovalLookupBudgetProvider} from "@/hooks/ApprovalLookupBudget";

interface ToolApprovalCardProps {
   position?: ApprovalPosition;
   /** Stacked reviews fold approved cards while retaining an inspectable summary. */
   collapseOnApproval?: boolean;
   embedded?: boolean;
   panel?: boolean;
   compactScript?: boolean;
   onOpenScript?: () => Promise<void>;
   call: MessageContentFunctionCall;
   completed: boolean;
   result?: unknown;
   active: boolean;
   disabled: boolean;
   onRespond?: (decision: ToolApprovalResponse) => Promise<ApprovalDecisionOutcome>;
   onRequestFresh?: (proposal: MutationProposal) => Promise<void>;
}

export default function ToolApprovalCard(props: ToolApprovalCardProps) {
   const preview = approvalPresentation(props.call);
   const {presentation, unavailable} = preview;
   const content = <ToolApprovalCardContent {...props} preview={preview} />;
   return presentation && !unavailable
      ? <ApprovalReferenceProvider presentation={presentation} parameters={props.call.parameters}>{content}</ApprovalReferenceProvider>
      : <ApprovalLookupBudgetProvider>{content}</ApprovalLookupBudgetProvider>;
}

function ToolApprovalCardContent({preview, compactScript = false, onOpenScript, embedded = false, panel = false, position, collapseOnApproval = false, call, completed, result, active, disabled, onRespond, onRequestFresh}: ToolApprovalCardProps & {preview: ReturnType<typeof approvalPresentation>}) {
   const {t} = useTranslation("ask");
   const targetId = useId();
   const contentId = useId();
   const decision = useToolApprovalDecision({call, completed, result, active, disabled, onRespond, onRequestFresh, preview});
   const {status, settled, expired, error, ownerOnly, saved, queued, canRequestFresh, requestFresh, requestedFresh} = decision;
   const collapse = useApprovalCollapse({collapseOnApproval, panel, approval: call.approval, decision});
   const {collapsed} = collapse;
   const approval = call.approval;
   if (!approval) return null;

   const model = approvalCardPresentation(call, approval, status, position, t, preview);
   const {unavailable, structured, title, statusKey} = model;
   const notices = <ToolApprovalNotices status={status} settled={settled} expired={expired} error={error} ownerOnly={ownerOnly} canRequestFresh={canRequestFresh} onRequestFresh={requestFresh} requestedFresh={requestedFresh}
      previewUnavailable={unavailable} queued={queued} remaining={position?.remaining} operation={structured ? "execution" : undefined} className="mx-4 my-3 text-sm" />;

   const publicationWorkflow = publicationWorkflowForApproval(call, unavailable, position);
   if (publicationWorkflow) return <WorkflowPublishApproval workflow={publicationWorkflow} call={call} decision={decision} result={result} notices={notices} />;

   const decisionControls = <ToolDecisionControls decision={decision} model={model} active={active} position={position}
      targetId={targetId} collapseOnApproval={collapseOnApproval} approve={collapse.approve} compactScript={compactScript} onOpenScript={onOpenScript}
      changeCount={approval.proposal ? changedFields(approval.proposal).length : 0} />;

   const receiptStatus = scriptReceiptStatus(decision);
   if (compactScript && approval.proposal && receiptStatus) return <ScriptApprovalReceipt proposal={approval.proposal}
      status={receiptStatus} saved={saved} onOpen={onOpenScript} notices={notices} />;

   return (
      <section aria-label={t(structured ? "toolApproval.execution.title" : "toolApproval.title")} data-approval-state={approvalVisualState(status, queued)} className={clsx("approval-card", cardLayoutClass(panel, embedded), selectionClass(queued) ?? "border-neutral bg-panel", structured && !panel && !collapseOnApproval && "max-w-xl")}>
         {!compactScript && !embedded && !collapsed && <ApprovalStatusBanner state={approvalVisualState(status, queued)}>
            {t(approvalBannerLabel(status, queued, statusKey))}
         </ApprovalStatusBanner>}
         {compactScript && approval.proposal ? <ScriptApprovalHeader proposal={approval.proposal} statusKey={statusKey} status={status} queued={queued} /> : <ToolCardHeader model={model} call={call} decision={decision} collapse={collapse}
            collapseOnApproval={collapseOnApproval} panel={panel} embedded={embedded} contentId={contentId} />}
         <div id={contentId} hidden={collapsed} className={panel ? "contents" : undefined}>
            {collapseOnApproval && decisionControls}
            {/* Keep long reviews keyboard-scrollable while the decision buttons stay outside the scroll region. */}
            <ApprovalReviewScrollArea enabled={panel} label={title}>
               <ToolReviewContent call={call} saved={saved} model={model} compactScript={compactScript} />
               {!panel && notices}
            </ApprovalReviewScrollArea>
            {panel && <div className="max-h-[30%] shrink-0 overflow-y-auto">{notices}</div>}
            {!collapseOnApproval && decisionControls}
         </div>
      </section>
   );
}

function publicationWorkflowForApproval(call: MessageContentFunctionCall, unavailable: boolean, position?: ApprovalPosition) {
   if (call.functionName !== "omega_publishcustomerworkflow" || unavailable || call.approval?.proposal) return null;
   if (position && position.total !== 1) return null;
   return readPublicationWorkflow(call.parameters);
}

function ToolApprovalContent({call, saved, compactScript}: {call: MessageContentFunctionCall; compactScript?: boolean; saved?: Record<string, unknown> | null}) {
   const approval = call.approval;
   if (!approval) return null;
   if (approval.proposal) {
      if (approval.proposal.capability === "use_cases_update") return <UseCaseSnapshotDiff preview={approval.proposal} saved={saved} />;
      return <ScriptSnapshotDiff preview={approval.proposal} compact={compactScript} />;
   }
   return null;
}

type ApprovalDecision = ReturnType<typeof useToolApprovalDecision>;
type CardPresentation = ReturnType<typeof approvalCardPresentation>;

function scriptReceiptStatus({status, pending, draft}: ApprovalDecision) {
   if (status === "completed" || status === "denied") return status;
   if (status === "approved" || (status === "submitting" && (pending === "apply" || draft === true))) return "saving";
   return undefined;
}

function decisionLabelKeys(batch: boolean, structured: boolean) {
   if (batch) return {approve: "toolApproval.batch.approveThis", deny: "toolApproval.batch.rejectThis"};
   if (structured) return {approve: "toolApproval.execution.approve", deny: "toolApproval.execution.cancel"};
   return {approve: "toolApproval.apply", deny: "toolApproval.discard"};
}

function approvalCardPresentation(call: MessageContentFunctionCall, approval: NonNullable<MessageContentFunctionCall["approval"]>, status: ApprovalDecision["status"], position: ApprovalPosition | undefined, t: TFunction<"ask">, preview: ReturnType<typeof approvalPresentation>) {
   const {presentation: validated, unavailable} = preview;
   const presentation = validated ? displayToolPresentation(validated, call.parameters, t) : undefined;
   const structured = !approval.proposal && !approval.previewUnavailable;
   let title = call.functionName;
   if (structured && presentation) title = presentationText(presentation.title, t);
   if (approval.proposal) title = proposalTitle(approval.proposal);
   const statusKey = approvalStatusKey(call, status, preview);
   const labels = decisionLabelKeys(!!position, structured);
   const approveLabel = !position && structured && presentation?.approveLabel
      ? presentationText(presentation.approveLabel, t) : t(labels.approve);
   const denyLabel = t(labels.deny);
   const body = toolRequestBody(call.parameters);
   const subject = [body.title, body.name].find((value): value is string => typeof value === "string" && !!value.trim()) ?? title;
   let description = t("toolApproval.previewDescription");
   if (structured) description = presentation?.description ? presentationText(presentation.description, t) : t("toolApproval.execution.description");

   return {presentation, unavailable, structured, title, statusKey, approveLabel, denyLabel, subject, description};
}

function cardLayoutClass(panel: boolean, embedded: boolean) {
   if (panel) return "flex h-full min-h-0 flex-col overflow-hidden";
   if (embedded) return "overflow-clip border-t";
   return "my-3 overflow-clip rounded-xl border";
}

function useApprovalCollapse({collapseOnApproval, panel, approval, decision}: {
   collapseOnApproval: boolean;
   panel: boolean;
   approval: MessageContentFunctionCall["approval"];
   decision: ApprovalDecision;
}) {
   const summaryRef = useRef<HTMLButtonElement>(null);
   const focusSummary = useRef(false);
   const [expanded, setExpanded] = useState(false);
   const {status, error, expired, queued, draft} = decision;
   const collapsible = collapseOnApproval && !panel && !error && !expired &&
      (queued === true || (status === "submitting" && draft === true) || approval?.approved === true || status === "completed") &&
      ["required", "submitting", "approved", "completed"].includes(status);
   const collapsed = collapsible && !expanded;
   useLayoutEffect(() => {
      if (!collapsed || !focusSummary.current) return;
      focusSummary.current = false;
      summaryRef.current?.focus({preventScroll: true});
      // Scroll only the review stack, without moving the surrounding conversation.
      const summary = summaryRef.current;
      if (!summary) return;
      for (let parent = summary.parentElement; parent; parent = parent.parentElement) {
         if (!/(auto|scroll)/.test(getComputedStyle(parent).overflowY)) continue;
         const item = summary.getBoundingClientRect();
         const viewport = parent.getBoundingClientRect();
         if (item.top < viewport.top) parent.scrollTop += item.top - viewport.top;
         else if (item.bottom > viewport.bottom) parent.scrollTop += item.bottom - viewport.bottom;
         break;
      }
   }, [collapsed]);
   const approve = async () => {
      focusSummary.current = collapseOnApproval;
      const outcome = await decision.respond(true);
      if (outcome === "queued" || outcome === "submitted") setExpanded(false);
      else focusSummary.current = false;
   };

   return {collapsible, collapsed, summaryRef, setExpanded, approve};
}

function ToolCardHeading({call, model, collapseOnApproval}: {
   call: MessageContentFunctionCall;
   model: CardPresentation;
   collapseOnApproval: boolean;
}) {
   const {structured, title, subject} = model;
   const useCase = structured && call.functionName === "use_cases_create" && collapseOnApproval;
   return <>
      <h3 className="approval-title break-words">{useCase ? subject : title}</h3>
      {useCase && <p className="approval-meta">{title}</p>}
      {call.approval?.proposal && call.approval.proposal.summary !== title && <p className="approval-meta">{call.approval.proposal.summary}</p>}
   </>;
}

function ToolCardHeader({model, call, decision, collapse, collapseOnApproval, panel, embedded, contentId}: {
   model: CardPresentation;
   call: MessageContentFunctionCall;
   decision: ApprovalDecision;
   collapse: ReturnType<typeof useApprovalCollapse>;
   collapseOnApproval: boolean;
   panel: boolean;
   embedded: boolean;
   contentId: string;
}) {
   const {structured, subject, statusKey} = model;
   const {queued, status} = decision;
   const {collapsible, collapsed, summaryRef, setExpanded} = collapse;
   if (collapsible) return <button ref={summaryRef} type="button" aria-expanded={!collapsed} aria-controls={contentId}
      onClick={() => setExpanded(value => !value)}
      className="flex w-full items-start gap-3 px-4 py-3.5 text-left hover:bg-100/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
      <span className="flex min-w-0 flex-1 flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
         <span className="approval-title break-words">{subject}</span>
         <ApprovalDecisionStatus queued={queued} statusKey={statusKey} saved={status === "completed"} />
      </span>
      <Icon icon={collapsed ? "chevron-down" : "chevron-up"} className="mt-1 h-3 w-3 shrink-0 text-500" />
   </button>;
   if (panel || (!structured && embedded)) return null;
   return <div className="approval-heading"><ToolCardHeading call={call} model={model} collapseOnApproval={collapseOnApproval} /></div>;
}

function ToolDecisionControls({decision, model, active, position, targetId, collapseOnApproval, approve, compactScript, onOpenScript, changeCount}: {
   decision: ApprovalDecision;
   model: CardPresentation;
   active: boolean;
   position?: ApprovalPosition;
   targetId: string;
   collapseOnApproval: boolean;
   approve: () => Promise<void>;
   compactScript?: boolean;
   onOpenScript?: () => Promise<void>;
   changeCount: number;
}) {
   const {t} = useTranslation("ask");
   const {queued, canRespond, canApply, respond} = decision;
   const {unavailable, denyLabel, approveLabel, description} = model;
   let decisionDescription = description;
   if (compactScript) decisionDescription = t("toolApproval.scriptReview.description", {count: changeCount});
   if (position) decisionDescription = t(position.remaining === 1 && queued == null ? "toolApproval.batch.finalDecision" : "toolApproval.batch.singleDescription", {count: position.total});
   if (decision.settled || !active) return null;
   return (
      <div className={clsx("approval-controls shrink-0 border-neutral bg-panel px-4 py-3", collapseOnApproval ? "sticky top-0 z-[1] border-b" : "border-t")}>
         {!unavailable && <p className="mb-2.5 text-xs leading-approval-meta text-600">{decisionDescription}</p>}
         <fieldset aria-labelledby={position ? targetId : undefined} className="flex min-w-0 flex-wrap items-center justify-end gap-2">
            {compactScript && onOpenScript && <span className="mr-auto"><OpenScriptButton onOpen={onOpenScript} /></span>}
            {position && <p id={targetId} className="mr-auto text-xs font-medium text-500">{t("toolApproval.batch.position", position)}</p>}
            <div className="flex min-w-0 flex-wrap justify-end gap-2">
               <Button type="tertiary" compact disabled={!canRespond} aria-pressed={queued === false} onClick={() => void respond(false)}
                  className="approval-reject aria-pressed:bg-100 aria-pressed:text-600"
                  iconBefore={queued === false ? <Icon icon="xmark" className="h-3 w-3" /> : undefined}>
                  {queued === false ? t(selectionLabelKey(false)) : denyLabel}
               </Button>
               <Button type={queued === true ? "tertiary" : "primary"} compact disabled={!canApply} aria-pressed={queued === true} onClick={() => void approve()}
                  className="approval-approve aria-pressed:bg-100 aria-pressed:text-600"
                  iconBefore={queued === true ? <Icon icon="check" className="h-3 w-3" /> : undefined}>
                  {queued === true ? t(selectionLabelKey(true)) : approveLabel}
               </Button>
            </div>
         </fieldset>
      </div>
   );
}

function ToolReviewContent({call, saved, model, compactScript}: {
   compactScript?: boolean;
   call: MessageContentFunctionCall;
   saved: ApprovalDecision["saved"];
   model: CardPresentation;
}) {
   if (!model.structured) return <ToolApprovalContent call={call} saved={saved} compactScript={compactScript} />;
   if (model.unavailable || !model.presentation) return null;
   return <div className="approval-details"><ToolDetailsContent presentation={model.presentation} parameters={call.parameters} /></div>;
}
